"use client";
// ServiceSection.js — AOS removed, flicker fixed (useLayoutEffect + conditional drawer render)
// Ready to paste

import React, { useEffect, useState, useLayoutEffect } from "react";
import {
    ArrowRight,
    Search,
    UserCheck,
    RefreshCcw,
    FileText,
    BarChart3,
    PieChart,
    Database,
    Copy,
    FolderSymlink,
    MessageSquare,
    Briefcase,
} from "lucide-react";
import servicesData from "./services.json"; // adjust path if needed

// copy area - edit only in services.json
const COPY = {
    title: "Our Services",
    subtitle: "Specialized support to reclaim, restore and grow your wealth.",
    showMoreBtn: { show: "Show other Services", hide: "Show fewer Services" },
};

const ICONS = {
    Search,
    UserCheck,
    RefreshCcw,
    FileText,
    BarChart3,
    PieChart,
    Database,
    Copy,
    FolderSymlink,
    MessageSquare,
    Briefcase,
};

const INITIAL_VISIBLE = 6;

export default function ServiceSection({ services = null }) {
    const mappedServices = (services || servicesData || []).map((s) => ({
        ...s,
        icon: ICONS[s.icon] || Search,
    }));

    const [showAll, setShowAll] = useState(false);
    const [activeService, setActiveService] = useState(null);
    const [isMobile, setIsMobile] = useState(false);

    // mounted flag prevents transition/paint flicker during hydration.
    // useLayoutEffect so it runs before the browser paints the first frame.
    const [mounted, setMounted] = useState(false);
    useLayoutEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 768);
        onResize();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") setActiveService(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => {
        document.body.style.overflow = activeService ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [activeService]);

    const visibleServices = showAll ? mappedServices : mappedServices.slice(0, INITIAL_VISIBLE);
    const hasMore = mappedServices.length > INITIAL_VISIBLE;

    const openDrawer = (service) => setActiveService(service);
    const closeDrawer = () => setActiveService(null);

    return (
        <section id="services" className="py-16 px-6 md:px-12 lg:px-36" style={{ backgroundColor: "#CDE9DC" }}>
            <div className="max-w-6xl mx-auto">
                <div className="mb-8 md:mb-12 text-center">
                    <h2 className="text-3xl md:text-4xl font-semibold" style={{ color: "var(--text-primary)" }}>
                        {COPY.title}
                    </h2>
                    <p className="mt-3 text-sm md:text-base max-w-2xl mx-auto" style={{ color: "var(--text-secondary)" }}>
                        {COPY.subtitle}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {visibleServices.map((s, idx) => {
                        const Icon = s.icon;
                        // small visual stagger using inline style delay (no AOS)
                        const delayMs = idx * 60;
                        return (
                            <article
                                key={s.id}
                                className="group relative block rounded-2xl p-6 md:p-8 will-change-transform bg-[var(--bg-surface)] border border-[var(--border)] hover:border-[var(--color-primary)] hover:shadow-lg"
                                style={{
                                    color: "var(--text-on-dark, #e5e7eb)",
                                    transitionDelay: `${delayMs}ms`,
                                }}
                            >
                                <div className="relative z-10 flex h-full flex-col justify-between">
                                    <div>
                                        <div
                                            className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors duration-300"
                                            style={{ backgroundColor: "rgba(255,255,255,0.04)", color: "var(--color-primary)" }}
                                        >
                                            <Icon className="w-6 h-6" aria-hidden />
                                        </div>

                                        <h3 className="text-xl md:text-2xl font-semibold transition-colors duration-300" style={{ color: "var(--text-primary, #2b2b2b)" }}>
                                            {s.title}
                                        </h3>
                                        <p className="mt-2 text-sm md:text-sm leading-relaxed transition-colors duration-300" style={{ color: "var(--text-muted, #b3b3b3)" }}>
                                            {s.description}
                                        </p>
                                    </div>

                                    <div className="mt-6 flex items-center justify-between">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openDrawer(s);
                                            }}
                                            className="text-xs font-medium uppercase hover:underline tracking-widest transition-colors duration-300"
                                            style={{ color: "var(--color-primary)", background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
                                        >
                                            LEARN MORE
                                        </button>

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openDrawer(s);
                                            }}
                                            className="inline-flex items-center gap-2 text-sm font-medium transition-all duration-300"
                                            style={{ color: "var(--color-primary)", background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
                                            aria-hidden
                                        >
                                            <span className="transform translate-x-0 group-hover:translate-x-1 transition-transform duration-300">
                                                <ArrowRight size={16} />
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>

                {hasMore && (
                    <div className="mt-8 text-center" style={{ transitionDelay: "80ms" }}>
                        <button
                            onClick={() => setShowAll((s) => !s)}
                            aria-expanded={showAll}
                            className="inline-flex items-center gap-3 px-5 py-3 rounded-full font-medium transition-shadow shadow-sm hover:shadow-md"
                            style={{ backgroundColor: "var(--bg-surface)", color: "var(--text-primary)", border: "1px solid var(--border)" }}
                        >
                            {showAll ? COPY.showMoreBtn.hide : COPY.showMoreBtn.show}
                        </button>
                    </div>
                )}
            </div>

            {/* Reduced motion support */}
            <style jsx>{`
        @media (prefers-reduced-motion: reduce) {
          .group,
          .group * {
            transition: none !important;
            animation: none !important;
          }
          .group:hover {
            transform: none !important;
            box-shadow: none !important;
          }
        }
      `}</style>

            {/* Only render overlay + drawer after mounted to prevent hydration paint issues */}
            {mounted && activeService && (
                <div>
                    {/* overlay */}
                    <div
                        className="fixed inset-0 z-40"
                        onClick={closeDrawer}
                        aria-hidden="true"
                        style={{ background: "rgba(0,0,0,0.36)" }}
                    />

                    {/* Drawer: right on desktop, bottom on mobile */}
                    <div
                        role="dialog"
                        aria-modal={!!activeService}
                        id={activeService ? `drawer-${activeService.id}` : undefined}
                        className={`fixed z-50 ${isMobile ? "left-0 right-0 bottom-0" : "top-0 bottom-0 right-0"}`}
                        style={{ pointerEvents: activeService ? "auto" : "none" }}
                    >
                        <div
                            className={`${isMobile
                                ? `max-h-[85vh] w-full ${activeService ? "translate-y-0" : "translate-y-full"}`
                                : `h-full w-[420px] ${activeService ? "translate-x-0" : "translate-x-full"}`
                                } transform transition-transform duration-300`}
                            style={{
                                backgroundColor: "var(--bg-surface)",
                                boxShadow: "0 20px 60px rgba(2,6,23,0.35)",
                                borderLeft: isMobile ? "none" : "1px solid var(--border)",
                                overflow: "auto",
                                borderTopLeftRadius: isMobile ? 12 : 0,
                                borderTopRightRadius: isMobile ? 12 : 0,
                                width: isMobile ? "100%" : 420,
                            }}
                        >
                            <div style={{ padding: 20 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                                    <div>
                                        <h3 style={{ margin: 0, color: "var(--color-primary)", fontSize: 20, fontWeight: 700 }}>{activeService?.title}</h3>
                                        <div style={{ marginTop: 6, color: "var(--text-secondary)", fontSize: 13, fontWeight: 600 }}>{activeService?.subtitle}</div>
                                    </div>

                                    <button onClick={closeDrawer} aria-label="Close" style={{ background: "transparent", border: "none", color: "var(--text-secondary)", fontSize: 18 }}>
                                        ✕
                                    </button>
                                </div>

                                <div style={{ marginTop: 14, color: "var(--text-primary)", fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-line" }}>{activeService?.long}</div>

                                <div style={{ marginTop: 18, borderTop: "1px solid var(--border)", paddingTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <div>
                                        <div style={{ color: "var(--text-secondary)", fontSize: 12 }}>Want help with this?</div>
                                        <div style={{ color: "var(--text-primary)", fontWeight: 600 }}>Request a consultation</div>
                                    </div>

                                    <button
                                        onClick={() => {
                                            closeDrawer();
                                            const el = document.getElementById("contact");
                                            if (el) el.scrollIntoView({ behavior: "smooth" });
                                        }}
                                        className="inline-flex items-center gap-2"
                                        style={{
                                            backgroundColor: "var(--color-primary)",
                                            color: "var(--bg-surface)",
                                            padding: "0.5rem 0.9rem",
                                            borderRadius: 8,
                                            textDecoration: "none",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Contact
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
